import moment, { Moment, unitOfTime } from 'moment';

import 'moment/locale/tr';
export type DateType = Moment;

export const DEFAULT_DATE_FORMAT = 'DD.MM.YYYY';
export const URL_DATE_FORMAT = 'YYYY-MM-DD';
export const HOTEL_SEARCH_DATE_FORMAT = URL_DATE_FORMAT;
export const FLIGHT_SEARCH_DATE_FORMAT = URL_DATE_FORMAT;
export const DEFAULT_DATE_TIME_FORMAT = 'DD.MM.YYYY HH:mm';
export const DATE_WITH_MONTH_TEXT_FORMAT = 'DD MMMM YYYY';
export const GTM_DATE_FORMAT = 'YYYYMMDD';
export const FORM_DATE_FORMAT = 'DD/MM/YYYY';

export const formatMomentDate = (
  momentDate: Date | Moment | null | undefined,
  toDateFormat: string = DEFAULT_DATE_FORMAT
): string =>
  momentDate
    ? `${moment(momentDate, DEFAULT_DATE_FORMAT).format(toDateFormat)}`
    : '';

export const formatISOToDate = (
  date: string,
  format = DEFAULT_DATE_FORMAT
): string => moment(date).format(format);

export const convertDateToMomentObject = (
  date: string | undefined = '',
  fromDateFormat: string = DEFAULT_DATE_FORMAT
) => moment(date || undefined, fromDateFormat);

export const convertStringDateFormat = (
  date: string | undefined = '',
  fromDateFormat: string,
  toDateFormat: string
) =>
  formatMomentDate(
    convertDateToMomentObject(date || undefined, fromDateFormat),
    toDateFormat
  );

export const getEndOfDay = () => moment().endOf('day');

export function isBeforeDay(a: DateType, b: DateType) {
  if (!moment.isMoment(a) || !moment.isMoment(b)) return false;

  const aYear = a.year();
  const aMonth = a.month();

  const bYear = b.year();
  const bMonth = b.month();

  const isSameYear = aYear === bYear;
  const isSameMonth = aMonth === bMonth;

  if (isSameYear && isSameMonth) return a.date() < b.date();
  if (isSameYear) return aMonth < bMonth;
  return aYear < bYear;
}

export function isBeforeDateTime(a: DateType, b: DateType) {
  if (!moment.isMoment(a) || !moment.isMoment(b)) return false;

  return a.isBefore(b);
}

export function isAfterDateTime(a: DateType, b: DateType) {
  if (!moment.isMoment(a) || !moment.isMoment(b)) return false;

  return a.isAfter(b);
}

export const getCurrentDate = () => {
  return moment();
};

export const getTomorrow = () => {
  return getNDaysLater(getCurrentDate(), 1);
};

export function isInclusivelyAfterDay(a: DateType, b: DateType) {
  if (!moment.isMoment(a) || !moment.isMoment(b)) return false;
  return !isBeforeDay(a, b);
}

export const getNMonthsLater = (fromDate: DateType, amount: number) => {
  return moment(fromDate).add(amount, 'month');
};

export const getNDaysLater = (fromDate: DateType, amount: number) => {
  return moment(fromDate).add(amount, 'days');
};

export const getNDaysLaterFromToday = (amount: number) => {
  return moment().add(amount, 'days');
};

export const getAge = (birthDate: string) =>
  moment().diff(formatISOToDate(birthDate), 'years');

export const getAgeFromFormattedString = (
  formattedBirthDate: string,
  format: string
) => {
  return moment().diff(moment(formattedBirthDate, format), 'years');
};

export const getTimeOfISODate = (date?: string): string =>
  date ? moment.utc(date).format('HH:mm') : '';

export const getLocaleDate = (date?: string) =>
  date
    ? new Date(date).toLocaleDateString('tr-TR', {
        year: 'numeric',
        month: 'long',
        day: '2-digit',
      })
    : '';

export const getWeekDayName = (
  date?: string,
  dateFormat = DEFAULT_DATE_FORMAT
) => moment(date, dateFormat).format('dddd');

export const getTimeDifferenceFromNow = (
  date?: string,
  unitOfTime: unitOfTime.Diff = 'days'
) => moment().diff(moment(date, DEFAULT_DATE_FORMAT), unitOfTime);

export const convertDateAsOnlyMonthString = (date: string) =>
  moment(date).format('Do MMMM YYYY');

export const getDaysDifference = (endDate: string, startDate: string) => {
  const startDiff = Math.abs(getTimeDifferenceFromNow(startDate));
  const endDiff = Math.abs(getTimeDifferenceFromNow(endDate));

  return endDiff - startDiff;
};

export const formatLongDate = (date: string) =>
  moment(date).format(DATE_WITH_MONTH_TEXT_FORMAT);

export const isDayFromThePast = (day: Moment) =>
  !isInclusivelyAfterDay(day, getCurrentDate());

export const getMoment = async () => {
  return (await import('moment')).default;
};

export function formatISODateGTM(inputDate: string) {
  // Create a Date object from the input string
  const dateObj = new Date(inputDate);

  // Extract the day, month, and year components from the Date object
  const day = dateObj.getUTCDate().toString().padStart(2, '0');
  const month = (dateObj.getUTCMonth() + 1).toString().padStart(2, '0'); // Add 1 to month since it's zero-indexed
  const year = dateObj.getUTCFullYear();

  // Concatenate the day, month, and year components into the format that we want
  const formattedDate = `${day}/${month}/${year}`;

  return formattedDate;
}

export const convertISODateToUnixTimestamp = (
  dateTimeString: string
): number => {
  const dateTime = new Date(dateTimeString);
  const unixTimestamp = Math.floor(dateTime.getTime() / 1000);
  return unixTimestamp;
};
export const getCurrentUnixTimestamp = (): number => {
  const currentDate = new Date();
  const unixTimestamp = Math.floor(currentDate.getTime() / 1000);
  return unixTimestamp;
};

export const getDateInUTCFormat = (
  date: string,
  format: string = DATE_WITH_MONTH_TEXT_FORMAT
) => {
  return moment.utc(date).format(format);
};

export const convertDateToSliceString = (date: string) => {
  //2024-08-15 -> 15.08.2024
  return `${date?.slice(8, 10)}.${date?.slice(5, 7)}.${date?.slice(0, 4)}`;
};
